/**
 * Operator Form Modal
 *
 * Modal for creating and editing factory operators.
 * Handles validation, submission, department assignment, and user linking.
 *
 * User linking enables Operator Portal access by connecting an operator
 * record to a user account with the 'operator' role.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  User,
  Hash,
  Mail,
  Building2,
  Loader2,
  AlertCircle,
  UserPlus,
  UserCog,
  Link2,
  Unlink,
  Sparkles,
  ChevronDown,
  CheckCircle2,
  Camera,
  Clock,
  Briefcase,
  MapPin,
} from 'lucide-react';
import { useCreateOperator, useUpdateOperator } from '../../hooks/useOperators';
import { useDepartmentLookup } from '../../hooks/useDepartments';
import { useShiftLookup } from '../../hooks/useShifts';
import { usePositionLookup } from '../../hooks/usePositions';
import { useStationLookup } from '../../hooks/useStations';
import { useUsersForOperatorLinking } from '../../hooks/useUsers';
import type { OperatorListItem } from '@guild/shared';

interface LinkedUser {
  id: string;
  email: string | null;
  fullName: string | null;
}

interface CreatedOperator {
  id: string;
  name: string;
}

interface OperatorFormProps {
  operator: (OperatorListItem & {
    userId?: string | null;
    linkedUser?: LinkedUser | null;
  }) | null;
  onClose: () => void;
}

export function OperatorForm({ operator, onClose }: OperatorFormProps) {
  const navigate = useNavigate();
  const isEditMode = !!operator;

  // Form state
  const [employeeId, setEmployeeId] = useState(operator?.employeeId ?? '');
  const [firstName, setFirstName] = useState(operator?.firstName ?? '');
  const [lastName, setLastName] = useState(operator?.lastName ?? '');
  const [email, setEmail] = useState(operator?.email ?? '');
  const [departmentId, setDepartmentId] = useState(operator?.departmentId ?? '');
  const [shiftId, setShiftId] = useState(operator?.shiftId ?? '');
  const [positionId, setPositionId] = useState(operator?.positionId ?? '');
  const [stationId, setStationId] = useState(operator?.stationId ?? '');

  // User linking state
  const [linkToUser, setLinkToUser] = useState(!!operator?.userId);
  const [selectedUserId, setSelectedUserId] = useState<string>(operator?.userId ?? '');
  const [autoPopulatedFields, setAutoPopulatedFields] = useState<Set<string>>(new Set());

  // Post-create dialog state
  const [createdOperator, setCreatedOperator] = useState<CreatedOperator | null>(null);

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Hooks
  const { data: departments } = useDepartmentLookup();
  const { data: shifts } = useShiftLookup();
  const { data: positions } = usePositionLookup();
  const { data: stations } = useStationLookup();
  const { data: availableUsers, isLoading: isLoadingUsers } = useUsersForOperatorLinking();
  const createMutation = useCreateOperator();
  const updateMutation = useUpdateOperator();

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  // Combine available users with current linked user (if editing and user is linked)
  const usersForDropdown = (() => {
    if (!availableUsers) return [];

    // If editing and operator has a linked user, add that user to the list
    // (since they won't appear in "available" users)
    if (isEditMode && operator?.linkedUser && operator.userId) {
      const existingLinkedUser = {
        id: operator.userId,
        fullName: operator.linkedUser.fullName ?? 'Unknown',
        email: operator.linkedUser.email ?? '',
        role: 'operator' as const,
      };

      // Check if already in list (shouldn't be, but safety check)
      const alreadyInList = availableUsers.some(u => u.id === operator.userId);
      if (!alreadyInList) {
        return [existingLinkedUser, ...availableUsers];
      }
    }

    return availableUsers;
  })();

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isSubmitting]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Auto-populate fields when user is selected
  useEffect(() => {
    if (selectedUserId && usersForDropdown.length > 0) {
      const user = usersForDropdown.find(u => u.id === selectedUserId);
      if (user) {
        // Parse fullName into firstName/lastName
        const nameParts = (user.fullName || '').trim().split(/\s+/);
        const newFirstName = nameParts[0] || '';
        const newLastName = nameParts.slice(1).join(' ') || '';

        // Track which fields were auto-populated
        const newAutoPopulated = new Set<string>();

        if (newFirstName && !firstName) {
          setFirstName(newFirstName);
          newAutoPopulated.add('firstName');
        }
        if (newLastName && !lastName) {
          setLastName(newLastName);
          newAutoPopulated.add('lastName');
        }
        if (user.email && !email) {
          setEmail(user.email);
          newAutoPopulated.add('email');
        }

        // Auto-generate employee ID if not already set
        if (!employeeId) {
          const firstInitial = (newFirstName[0] || '').toUpperCase();
          const lastInitial = (newLastName[0] || '').toUpperCase();
          const randomNum = Math.floor(1000 + Math.random() * 9000); // 4-digit number
          const generatedId = `OP-${firstInitial}${lastInitial}${randomNum}`;
          setEmployeeId(generatedId);
          newAutoPopulated.add('employeeId');
        }

        setAutoPopulatedFields(newAutoPopulated);

        // Clear auto-populated indicator after a delay
        if (newAutoPopulated.size > 0) {
          setTimeout(() => setAutoPopulatedFields(new Set()), 3000);
        }
      }
    }
  }, [selectedUserId, usersForDropdown, firstName, lastName, email, employeeId]);

  // Clear selection when unchecking link to user
  useEffect(() => {
    if (!linkToUser) {
      setSelectedUserId('');
      setAutoPopulatedFields(new Set());
    }
  }, [linkToUser]);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!employeeId.trim()) {
      newErrors.employeeId = 'Employee ID is required';
    } else if (employeeId.length > 50) {
      newErrors.employeeId = 'Employee ID must be 50 characters or less';
    }

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (firstName.length > 100) {
      newErrors.firstName = 'First name must be 100 characters or less';
    }

    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (lastName.length > 100) {
      newErrors.lastName = 'Last name must be 100 characters or less';
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [employeeId, firstName, lastName, email]);

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) return;

    try {
      // For create mode, use undefined for optional fields (means "not provided")
      // For edit mode, use null for fields that should be cleared
      const formData = {
        employeeId: employeeId.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || undefined,
        departmentId: departmentId || undefined,
        shiftId: shiftId || undefined,
        positionId: positionId || undefined,
        stationId: stationId || undefined,
        userId: linkToUser && selectedUserId ? selectedUserId : undefined,
      };

      if (isEditMode) {
        // In edit mode, explicitly send null to clear optional fields
        // undefined means "no change", null means "clear the field"
        await updateMutation.mutateAsync({
          id: operator.id,
          data: {
            ...formData,
            // Use null to clear fields, empty string means "clear"
            departmentId: departmentId || null,
            shiftId: shiftId || null,
            positionId: positionId || null,
            stationId: stationId || null,
            // Explicitly unlink if checkbox is unchecked but was previously linked
            userId: linkToUser ? (selectedUserId || null) : null,
          },
        });
        onClose();
      } else {
        // Create mode - show post-create dialog
        const result = await createMutation.mutateAsync(formData);
        setCreatedOperator({
          id: result.id,
          name: `${firstName.trim()} ${lastName.trim()}`,
        });
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        setSubmitError(error.message);
      } else {
        setSubmitError('An unexpected error occurred');
      }
    }
  };

  // Handle post-create dialog actions
  const handleAddPhotosNow = () => {
    if (createdOperator) {
      navigate(`/operators/${createdOperator.id}?tab=photos`);
    }
  };

  const handleDone = () => {
    setCreatedOperator(null);
    onClose();
  };

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  // Helper to check if field was auto-populated
  const isAutoPopulated = (field: string) => autoPopulatedFields.has(field);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="
          w-full max-w-lg max-h-[90vh] overflow-y-auto
          rounded-2xl bg-white shadow-2xl
          animate-in fade-in zoom-in-95 duration-200
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-guild-green to-guild-green/80 shadow-lg shadow-guild-green/20">
              {isEditMode ? (
                <UserCog size={20} className="text-white" strokeWidth={2} />
              ) : (
                <UserPlus size={20} className="text-white" strokeWidth={2} />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {isEditMode ? 'Edit Operator' : 'Add Operator'}
              </h2>
              <p className="text-xs text-gray-500">
                {isEditMode ? 'Update operator details' : 'Register a new factory operator'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="
              rounded-lg p-2 text-gray-400
              transition-colors duration-200
              hover:bg-gray-100 hover:text-gray-600
              disabled:cursor-not-allowed disabled:opacity-50
            "
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* User Linking Section */}
          <div className="rounded-xl border border-guild-mint/50 bg-gradient-to-br from-guild-mint/10 to-white p-4 space-y-3">
            <div className="flex items-start gap-3">
              <label className="flex items-center gap-3 cursor-pointer group flex-1">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={linkToUser}
                    onChange={(e) => setLinkToUser(e.target.checked)}
                    disabled={isSubmitting}
                    className="
                      peer sr-only
                    "
                  />
                  <div className="
                    h-5 w-5 rounded-md border-2 border-gray-300 bg-white
                    flex items-center justify-center
                    transition-all duration-200
                    peer-checked:border-guild-green peer-checked:bg-guild-green
                    peer-focus-visible:ring-2 peer-focus-visible:ring-guild-green/20 peer-focus-visible:ring-offset-2
                    group-hover:border-guild-green/50
                    peer-disabled:cursor-not-allowed peer-disabled:opacity-60
                  ">
                    {linkToUser && (
                      <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M2 6l3 3 5-6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link2 size={16} className="text-guild-green" />
                  <span className="text-sm font-semibold text-gray-700">
                    Link to user account
                  </span>
                </div>
              </label>
            </div>

            {!linkToUser && (
              <p className="text-xs text-gray-500 ml-8 leading-relaxed">
                Link to a user account to enable{' '}
                <span className="font-medium text-guild-green">Operator Portal</span>{' '}
                access for this operator.
              </p>
            )}

            {/* User Dropdown - shown when checkbox is checked */}
            {linkToUser && (
              <div className="ml-8 space-y-2 animate-in slide-in-from-top-2 duration-200">
                {/* Current linked user indicator (edit mode) */}
                {isEditMode && operator?.linkedUser && operator.userId === selectedUserId && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-guild-green/10">
                      <User size={12} className="text-guild-green" />
                    </div>
                    <span>
                      Currently linked to{' '}
                      <span className="font-medium text-gray-700">
                        {operator.linkedUser.fullName}
                      </span>
                    </span>
                  </div>
                )}

                <div className="relative">
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    disabled={isSubmitting || isLoadingUsers}
                    className="
                      w-full appearance-none rounded-xl border border-gray-200 bg-white
                      pl-4 pr-10 py-2.5 text-sm text-gray-900
                      transition-all duration-200
                      focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                      disabled:cursor-not-allowed disabled:opacity-60
                    "
                  >
                    <option value="">
                      {isLoadingUsers ? 'Loading users...' : 'Select a user account...'}
                    </option>
                    {usersForDropdown.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.fullName} ({user.email})
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                </div>

                {usersForDropdown.length === 0 && !isLoadingUsers && (
                  <p className="text-xs text-amber-600 flex items-center gap-1.5">
                    <AlertCircle size={12} />
                    No users with 'operator' role available for linking.
                  </p>
                )}

                {/* Unlink button for edit mode */}
                {isEditMode && operator?.userId && selectedUserId && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUserId('');
                      setLinkToUser(false);
                    }}
                    className="
                      flex items-center gap-1.5 text-xs text-gray-500
                      hover:text-rose-600 transition-colors
                    "
                  >
                    <Unlink size={12} />
                    Unlink user account
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200/70" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                Operator Details
              </span>
            </div>
          </div>

          {/* Employee ID */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Hash size={14} className="text-gray-400" />
              Employee ID
              <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={employeeId}
              onChange={(e) => {
                setEmployeeId(e.target.value);
                if (errors.employeeId) setErrors((prev) => ({ ...prev, employeeId: '' }));
              }}
              placeholder="e.g., EMP-001"
              disabled={isSubmitting}
              className={`
                w-full rounded-xl border bg-white
                px-4 py-2.5 text-sm text-gray-900
                placeholder-gray-400
                transition-all duration-200
                focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                disabled:cursor-not-allowed disabled:opacity-60
                ${errors.employeeId ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-500/20' : 'border-gray-200'}
              `}
            />
            {errors.employeeId && (
              <p className="flex items-center gap-1 text-xs text-rose-500">
                <AlertCircle size={12} />
                {errors.employeeId}
              </p>
            )}
          </div>

          {/* First Name & Last Name - Side by Side */}
          <div className="grid grid-cols-2 gap-4">
            {/* First Name */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <User size={14} className="text-gray-400" />
                First Name
                <span className="text-rose-500">*</span>
                {isAutoPopulated('firstName') && (
                  <span className="flex items-center gap-1 text-[10px] font-medium text-guild-green animate-in fade-in duration-300">
                    <Sparkles size={10} />
                    auto-filled
                  </span>
                )}
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (errors.firstName) setErrors((prev) => ({ ...prev, firstName: '' }));
                  setAutoPopulatedFields(prev => {
                    const next = new Set(prev);
                    next.delete('firstName');
                    return next;
                  });
                }}
                placeholder="John"
                disabled={isSubmitting}
                className={`
                  w-full rounded-xl border bg-white
                  px-4 py-2.5 text-sm text-gray-900
                  placeholder-gray-400
                  transition-all duration-200
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:cursor-not-allowed disabled:opacity-60
                  ${isAutoPopulated('firstName') ? 'border-guild-green/50 bg-guild-mint/5' : ''}
                  ${errors.firstName ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-500/20' : 'border-gray-200'}
                `}
              />
              {errors.firstName && (
                <p className="flex items-center gap-1 text-xs text-rose-500">
                  <AlertCircle size={12} />
                  {errors.firstName}
                </p>
              )}
            </div>

            {/* Last Name */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                Last Name
                <span className="text-rose-500">*</span>
                {isAutoPopulated('lastName') && (
                  <span className="flex items-center gap-1 text-[10px] font-medium text-guild-green animate-in fade-in duration-300">
                    <Sparkles size={10} />
                    auto-filled
                  </span>
                )}
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (errors.lastName) setErrors((prev) => ({ ...prev, lastName: '' }));
                  setAutoPopulatedFields(prev => {
                    const next = new Set(prev);
                    next.delete('lastName');
                    return next;
                  });
                }}
                placeholder="Doe"
                disabled={isSubmitting}
                className={`
                  w-full rounded-xl border bg-white
                  px-4 py-2.5 text-sm text-gray-900
                  placeholder-gray-400
                  transition-all duration-200
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:cursor-not-allowed disabled:opacity-60
                  ${isAutoPopulated('lastName') ? 'border-guild-green/50 bg-guild-mint/5' : ''}
                  ${errors.lastName ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-500/20' : 'border-gray-200'}
                `}
              />
              {errors.lastName && (
                <p className="flex items-center gap-1 text-xs text-rose-500">
                  <AlertCircle size={12} />
                  {errors.lastName}
                </p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Mail size={14} className="text-gray-400" />
              Email
              <span className="text-xs font-normal text-gray-400">(optional)</span>
              {isAutoPopulated('email') && (
                <span className="flex items-center gap-1 text-[10px] font-medium text-guild-green animate-in fade-in duration-300">
                  <Sparkles size={10} />
                  auto-filled
                </span>
              )}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
                setAutoPopulatedFields(prev => {
                  const next = new Set(prev);
                  next.delete('email');
                  return next;
                });
              }}
              placeholder="john.doe@company.com"
              disabled={isSubmitting}
              className={`
                w-full rounded-xl border bg-white
                px-4 py-2.5 text-sm text-gray-900
                placeholder-gray-400
                transition-all duration-200
                focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                disabled:cursor-not-allowed disabled:opacity-60
                ${isAutoPopulated('email') ? 'border-guild-green/50 bg-guild-mint/5' : ''}
                ${errors.email ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-500/20' : 'border-gray-200'}
              `}
            />
            {errors.email && (
              <p className="flex items-center gap-1 text-xs text-rose-500">
                <AlertCircle size={12} />
                {errors.email}
              </p>
            )}
          </div>

          {/* Department */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Building2 size={14} className="text-gray-400" />
              Department
              <span className="text-xs font-normal text-gray-400">(optional)</span>
            </label>
            <div className="relative">
              <select
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
                disabled={isSubmitting}
                className="
                  w-full appearance-none rounded-xl border border-gray-200 bg-white
                  pl-4 pr-10 py-2.5 text-sm text-gray-900
                  transition-all duration-200
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:cursor-not-allowed disabled:opacity-60
                "
              >
                <option value="">No department assigned</option>
                {departments?.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>
          </div>

          {/* Zone (Phase 19) */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <MapPin size={14} className="text-orange-500" />
              Zone
              <span className="text-xs font-normal text-gray-400">(optional)</span>
            </label>
            <div className="relative">
              <select
                value={stationId}
                onChange={(e) => setStationId(e.target.value)}
                disabled={isSubmitting}
                className="
                  w-full appearance-none rounded-xl border border-gray-200 bg-white
                  pl-4 pr-10 py-2.5 text-sm text-gray-900
                  transition-all duration-200
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:cursor-not-allowed disabled:opacity-60
                "
              >
                <option value="">No zone assigned</option>
                {stations?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>
          </div>

          {/* Shift & Position - Side by Side */}
          <div className="grid grid-cols-2 gap-4">
            {/* Shift */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Clock size={14} className="text-blue-500" />
                Shift
                <span className="text-xs font-normal text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <select
                  value={shiftId}
                  onChange={(e) => setShiftId(e.target.value)}
                  disabled={isSubmitting}
                  className="
                    w-full appearance-none rounded-xl border border-gray-200 bg-white
                    pl-4 pr-10 py-2.5 text-sm text-gray-900
                    transition-all duration-200
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:cursor-not-allowed disabled:opacity-60
                  "
                >
                  <option value="">No shift assigned</option>
                  {shifts?.map((shift) => (
                    <option key={shift.id} value={shift.id}>
                      {shift.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            </div>

            {/* Position */}
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Briefcase size={14} className="text-purple-500" />
                Position
                <span className="text-xs font-normal text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <select
                  value={positionId}
                  onChange={(e) => setPositionId(e.target.value)}
                  disabled={isSubmitting}
                  className="
                    w-full appearance-none rounded-xl border border-gray-200 bg-white
                    pl-4 pr-10 py-2.5 text-sm text-gray-900
                    transition-all duration-200
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:cursor-not-allowed disabled:opacity-60
                  "
                >
                  <option value="">No position assigned</option>
                  {positions?.map((pos) => (
                    <option key={pos.id} value={pos.id}>
                      {pos.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            </div>
          </div>

          {/* Submit Error */}
          {submitError && (
            <div className="flex items-center gap-2 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-600">
              <AlertCircle size={16} className="flex-shrink-0" />
              {submitError}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="
                rounded-xl border border-gray-200 bg-white
                px-5 py-2.5 text-sm font-semibold text-gray-700
                transition-all duration-200
                hover:bg-gray-50 hover:border-gray-300
                disabled:cursor-not-allowed disabled:opacity-50
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="
                inline-flex items-center gap-2 rounded-xl
                bg-guild-green px-5 py-2.5
                text-sm font-semibold text-white
                shadow-lg shadow-guild-green/25
                transition-all duration-200
                hover:bg-guild-green/90 hover:shadow-xl
                disabled:cursor-not-allowed disabled:opacity-70
              "
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {isEditMode ? 'Saving...' : 'Creating...'}
                </>
              ) : isEditMode ? (
                'Save Changes'
              ) : (
                'Add Operator'
              )}
            </button>
          </div>
        </form>

        {/* Post-Create Success Dialog */}
        {createdOperator && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/95 backdrop-blur-sm rounded-2xl">
            <div className="flex flex-col items-center text-center p-8 animate-in fade-in zoom-in-95 duration-300">
              {/* Success Icon */}
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 mb-4">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Operator Created!
              </h3>

              {/* Description */}
              <p className="text-sm text-gray-500 mb-6 max-w-xs">
                <span className="font-semibold text-gray-700">{createdOperator.name}</span>{' '}
                has been added successfully.
              </p>

              {/* Photo Upload Prompt */}
              <div className="flex flex-col items-center gap-4 w-full">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Camera size={16} className="text-guild-green" />
                  <span>Would you like to add photos for face enrollment?</span>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-3 w-full max-w-xs">
                  <button
                    onClick={handleDone}
                    className="
                      flex-1 rounded-xl border border-gray-200 bg-white
                      px-4 py-2.5 text-sm font-semibold text-gray-700
                      transition-all duration-200
                      hover:bg-gray-50 hover:border-gray-300
                    "
                  >
                    Done
                  </button>
                  <button
                    onClick={handleAddPhotosNow}
                    className="
                      flex-1 inline-flex items-center justify-center gap-2 rounded-xl
                      bg-guild-green px-4 py-2.5
                      text-sm font-semibold text-white
                      shadow-lg shadow-guild-green/25
                      transition-all duration-200
                      hover:bg-guild-green/90 hover:shadow-xl
                    "
                  >
                    <Camera size={16} />
                    Add Photos
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OperatorForm;
